import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import {
  getBlockByID,
  getAllAccommodationsByProject,
  getSalesManagerUsers,
  setUnitParkingLinks
} from '../../services/api/implementation/impl';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import ParkingAllotmentPanel from './ParkingAllotmentPanel';

export const EditUnit = ({
  handleEditUnit,
  unitId,
  activeLevelType,
  data,
  onClose,
  isSubmitting
}) => {
  const userId = Cookies.get('userId');
  const userVertical = Cookies.get('userVertical');
  const userLevel = Cookies.get('userDesignationLevel');
  const [filteredAccommodations, setFilteredAccommodations] = useState([]);
  const [parkingSelection, setParkingSelection] = useState(null);
  const [isSavingParking, setIsSavingParking] = useState(false);

  const canAssignSalesManager =
    userVertical === 'Management' ||
    (userVertical === 'Sales' && (userLevel === 'High' || userLevel === 'Medium'));

  const {
    data: blockDyna,
    refetch: refetchBlockDyna,
    isLoading: isBlockDynaLoading
  } = useQuery({
    queryKey: ['block', data?.[0]?.block_id],
    queryFn: () => getBlockByID(data?.[0]?.block_id),
    enabled: !!data?.[0]?.block_id
  });

  const { data: accommodations } = useQuery({
    queryKey: ['accommodations', data?.[0]?.project_id],
    queryFn: () => getAllAccommodationsByProject(data?.[0]?.project_id),
    enabled: !!data?.[0]?.project_id
  });

  const { data: salesManagersData } = useQuery({
    queryKey: ['salesManagerUsers', data?.[0]?.project_id],
    queryFn: () =>
      getSalesManagerUsers(1, 500, data?.[0]?.project_id ?? null, null),
    enabled: canAssignSalesManager && !!data?.[0]?.project_id
  });

  const salesManagerOptions = useMemo(() => {
    const all = salesManagersData?.data ?? [];
    if (userVertical === 'Management' || (userVertical === 'Sales' && userLevel === 'High')) {
      return all;
    }
    if (userVertical === 'Sales' && userLevel === 'Medium') {
      return all.filter((u) => String(u.id) === String(userId));
    }
    return [];
  }, [salesManagersData?.data, userVertical, userLevel, userId]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((item) => parseInt(item.id, 10) === parseInt(unitId, 10));
  }, [data, unitId]);

  const currentUnit = filteredData[0] || {};
  const projectId = currentUnit.project_id;
  const projectDefaultIncludeCost = !!currentUnit.project?.include_linked_parking_cost;

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      unit_name: '',
      accomodation_id: '',
      carpetArea: 0,
      buildUpArea: 0,
      area: '',
      loadingFactor: '',
      superBuildUpArea: '',
      electrified: 'No',
      sales_manager_id: ''
    }
  });

  useEffect(() => {
    mixpanel.track('Edit Unit Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (!filteredData.length || !accommodations || !blockDyna || isBlockDynaLoading) {
      return;
    }

    const accommodationList = Array.isArray(accommodations)
      ? accommodations
      : Object.values(accommodations || {}).flat();

    let filtered = [];
    const blockType = blockDyna?.type;
    const unit = filteredData[0] || {};
    const accom = unit?.accommodation || {};
    // Prefer relation id; fall back to FK so edit form keeps the saved parking type
    // (API lists accommodations id DESC, so an empty select defaults to the latest).
    const savedAccomId = accom?.id ?? unit.accomodation_id ?? unit.accommodation_id ?? null;

    if (activeLevelType === 'PARKING') {
      filtered = accommodationList.filter(
        (acc) => acc.accomodationType?.toLowerCase() === 'parking'
      );
    } else {
      const unitType = accom?.unitType || activeLevelType;

      filtered = accommodationList.filter(
        (acc) =>
          acc.accomodationType?.toLowerCase() === blockType?.toLowerCase() &&
          acc.unitType?.toLowerCase() === unitType?.toLowerCase()
      );

      if (!blockType || filtered.length === 0) {
        filtered = accommodationList.filter(
          (acc) => acc.unitType?.toLowerCase() === unitType?.toLowerCase()
        );
      }
    }

    // Keep the unit's current accommodation in the dropdown even if filter rules miss it
    if (
      savedAccomId &&
      !filtered.some((acc) => Number(acc.id) === Number(savedAccomId))
    ) {
      const savedAcc =
        (accom?.id && Number(accom.id) === Number(savedAccomId) ? accom : null) ||
        accommodationList.find((acc) => Number(acc.id) === Number(savedAccomId));
      if (savedAcc) {
        filtered = [savedAcc, ...filtered];
      }
    }

    setFilteredAccommodations(filtered);

    const salesManagerId =
      unit.salesManager?.id ??
      unit.sales_manager_id ??
      unit.sales_manager?.id ??
      '';

    reset({
      unit_name: unit.unitName || '',
      accomodation_id: savedAccomId ? String(savedAccomId) : '',
      carpetArea: accom.carpetArea ?? 0,
      buildUpArea: accom.buildUpArea ?? 0,
      area: accom.buildUpArea ?? '',
      loadingFactor: accom.loadingFactor ?? '',
      superBuildUpArea: accom.superBuildUpArea ?? '',
      electrified: accom.electrified ? 'Yes' : 'No',
      sales_manager_id: salesManagerId ? String(salesManagerId) : ''
    });
  }, [filteredData, accommodations, blockDyna, isBlockDynaLoading, activeLevelType, reset]);

  useEffect(() => {
    if (data?.[0]?.block_id) {
      refetchBlockDyna(data[0].block_id);
    }
  }, [data, refetchBlockDyna]);

  const onSubmit = async (formData) => {
    const payload = {
      unit_name: formData.unit_name,
      accomodation_id: parseInt(formData.accomodation_id, 10) || null,
      carpetArea: parseFloat(formData.carpetArea) || 0,
      buildUpArea: parseFloat(formData.buildUpArea) || 0,
      electrified: formData.electrified === 'Yes'
    };

    if (canAssignSalesManager && formData.sales_manager_id) {
      payload.sales_manager_id = parseInt(formData.sales_manager_id, 10);
    }

    if (activeLevelType !== 'PARKING' && parkingSelection) {
      try {
        setIsSavingParking(true);
        await setUnitParkingLinks(unitId, {
          parking_unit_ids: parkingSelection.parking_unit_ids || [],
          include_cost_by_parking_id: parkingSelection.include_cost_by_parking_id || {},
          include_cost: projectDefaultIncludeCost
        });
      } catch (err) {
        toast.error(
          err?.response?.data?.message || err?.message || 'Failed to save parking links'
        );
        setIsSavingParking(false);
        return;
      } finally {
        setIsSavingParking(false);
      }
    }

    handleEditUnit(payload);
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-2 gap-5">
        <FormInput
          width={100}
          register={register}
          register_key="unit_name"
          label="Unit Name"
          type="text"
          isRequired
        />

        <FormInput
          key={`accomodation_id-${unitId}-${
            currentUnit?.accommodation?.id ??
            currentUnit?.accomodation_id ??
            filteredAccommodations.length
          }`}
          width={100}
          register={register}
          register_key="accomodation_id"
          label={activeLevelType === 'PARKING' ? 'Parking Type' : 'Accommodation Name'}
          type="dropdown"
          options={filteredAccommodations.map((acc) => {
            const savedId =
              currentUnit?.accommodation?.id ??
              currentUnit?.accomodation_id ??
              currentUnit?.accommodation_id;
            return {
              value: String(acc.id),
              label: acc.name,
              selected: savedId != null && String(acc.id) === String(savedId)
            };
          })}
        />

        {activeLevelType !== 'PARKING' && (
          <FormInput
            width={100}
            register={register}
            register_key="carpetArea"
            label="Carpet Area"
            type="text"
            disabled
            valuePre={filteredData[0]?.accommodation?.carpetArea || 0}
          />
        )}

        {activeLevelType !== 'PARKING' && (
          <FormInput
            width={100}
            register={register}
            register_key="buildUpArea"
            label="Build up Area"
            type="text"
            disabled
            valuePre={filteredData[0]?.accommodation?.buildUpArea || 0}
          />
        )}

        {activeLevelType === 'PARKING' && (
          <FormInput
            key={`area-${filteredData[0]?.area || ''}-${unitId}`}
            width={100}
            register={register}
            register_key="area"
            label="Area"
            type="text"
            disabled
            valuePre={filteredData[0]?.accommodation?.buildUpArea || ''}
          />
        )}

        {activeLevelType === 'COMMERCIAL' && (
          <FormInput
            width={100}
            register={register}
            register_key="loadingFactor"
            label="Loading Factor"
            type="text"
            disabled
            valuePre={filteredData[0]?.accommodation?.loadingFactor || ''}
          />
        )}

        {activeLevelType === 'COMMERCIAL' && (
          <FormInput
            width={100}
            register={register}
            register_key="superBuildUpArea"
            label="Super Build Up"
            type="text"
            disabled
            valuePre={filteredData[0]?.accommodation?.superBuildUpArea || 0}
          />
        )}

        {activeLevelType === 'PARKING' && (
          <FormInput
            width={100}
            register={register}
            register_key="electrified"
            label="Electrified"
            type="dropdown"
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' }
            ]}
          />
        )}

        {canAssignSalesManager && activeLevelType !== 'PARKING' && (
          <FormInput
            width={100}
            register={register}
            register_key="sales_manager_id"
            label="Sales Manager"
            type="dropdown"
            options={[
              { value: '', label: 'Unassigned' },
              ...salesManagerOptions.map((u) => ({
                value: String(u.id),
                label: u.name || u.email || `User ${u.id}`
              }))
            ]}
          />
        )}

        {!!projectId && !!unitId && (
          <ParkingAllotmentPanel
            projectId={projectId}
            flatUnitId={unitId}
            mode={activeLevelType === 'PARKING' ? 'parking' : 'flat'}
            defaultIncludeCost={projectDefaultIncludeCost}
            value={parkingSelection}
            onChange={setParkingSelection}
            disabled={isSubmitting || isSavingParking}
          />
        )}
      </div>

      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button type="reset" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          type="submit"
          disabled={isSubmitting || isSavingParking}>
          Save
        </Button>
      </div>
    </div>
  );
};
